import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import packageJson from '../../../package.json';

// The running build's version is baked in at build time (the pipeline stamps package.json
// from AltopPermissions.Applications.latestVersion). In the repo it's a placeholder.
const BUILD_VERSION = packageJson.version;
const PLACEHOLDER = '99.99.99';
const POLL_MS = 5 * 60 * 1000; // re-check every 5 min (cheap — just a static /version.json fetch)

// true only if `latest` is strictly newer than `current` (numeric semver compare).
const isNewer = (latest, current) => {
  if (!latest || !current || current === PLACEHOLDER) return false;
  const a = String(latest).split('.').map((n) => parseInt(n, 10) || 0);
  const b = String(current).split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] || 0;
    const y = b[i] || 0;
    if (x > y) return true;
    if (x < y) return false;
  }
  return false;
};

const VersionBanner = ({ appName }) => {
  const theme = useTheme();
  const [latest, setLatest] = useState(null);
  const [stale, setStale] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const check = useCallback(async () => {
    try {
      // Static file written into the build folder by the deploy pipeline (no backend call).
      // no-store so we always read the freshly deployed value, never a cached copy.
      const res = await fetch('/version.json', { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const lv = data && data.version ? String(data.version) : null;
      if (lv && isNewer(lv, BUILD_VERSION)) {
        setLatest(lv);
        setStale(true);
      }
    } catch {
      /* best-effort: a version-check failure must never break the app */
    }
  }, []);

  useEffect(() => {
    // Once a newer version is detected the banner stays up, so stop pinging the server.
    if (stale) return undefined;
    check();
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    const id = setInterval(check, POLL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(id);
    };
  }, [check, stale]);

  if (!stale || dismissed) return null;

  const isDark = theme?.palette?.mode === 'dark';
  const c = isDark
    ? { bg: '#2A2200', fg: '#FFD27A', border: 'rgba(239,127,26,0.40)' }
    : { bg: '#FFF4E0', fg: '#7A4F00', border: 'rgba(239,127,26,0.50)' };

  return (
    <Box
      role="alert"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1800,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 0.75,
        backgroundColor: c.bg,
        color: c.fg,
        borderBottom: `1px solid ${c.border}`,
        fontFamily: 'Montserrat, sans-serif',
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
        A new version of {appName} is available (v{latest}). Current: v{BUILD_VERSION}.
      </Typography>
      <Button
        size="small"
        variant="contained"
        onClick={() => window.location.reload()}
        sx={{
          textTransform: 'none',
          backgroundColor: '#EF7F1A',
          color: '#fff',
          '&:hover': { backgroundColor: '#d96f12' },
        }}
      >
        Reload
      </Button>
      <Box sx={{ flexGrow: 1 }} />
      <IconButton size="small" aria-label="Dismiss update notice" onClick={() => setDismissed(true)} sx={{ color: c.fg }}>
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

export default VersionBanner;
